import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  EmptyInputComponent,
  ModalComponent,
  TextInputComponent,
  ToggleInputComponent,
} from 'common';
import { TranslateModule } from '@ngx-translate/core';
import { provideIcons } from '@ng-icons/core';
import { heroInformationCircleMini } from '@ng-icons/heroicons/mini';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { SupportService } from '../../../service';

export interface SupportContactData {
  name?: string;
  email?: string;
  tgHandle?: string;
  info?: string;
}

function emailOrTelegramValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent) {
      return null;
    }

    const email = control.parent.get('email')?.value;
    const tgHandle = control.parent.get('tgHandle')?.value;

    if (!email && !tgHandle) {
      return { emailOrTelegramRequired: true };
    }

    return null;
  };
}

@Component({
  selector: 'common-rebalancing-support-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    TranslateModule,
    ReactiveFormsModule,
    TextInputComponent,
    EmptyInputComponent,
    ToggleInputComponent,
  ],
  providers: [
    provideIcons({
      heroInformationCircleMini,
    }),
  ],
  templateUrl: './support-modal.component.html',
  styleUrls: ['./support-modal.component.scss'],
})
export class SupportModalComponent {
  public static readonly STORAGE_KEY_HIDE = 'hide-support-modal';

  @Input()
  public isOpen = false;

  @Output()
  public isOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  public contactForm: FormGroup;
  public showContactForm = false;
  public contactFormSent = false;

  public dontShowAgain = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly supportService: SupportService,
  ) {
    this.contactForm = this.fb.group({
      name: [''],
      email: ['', [Validators.email, emailOrTelegramValidator()]],
      tgHandle: ['', [emailOrTelegramValidator()]],
      info: [''],
    });

    // Re-validate both fields when either changes
    this.contactForm.get('email')?.valueChanges.subscribe(() => {
      this.contactForm
        .get('tgHandle')
        ?.updateValueAndValidity({ emitEvent: false });
    });

    this.contactForm.get('tgHandle')?.valueChanges.subscribe(() => {
      this.contactForm
        .get('email')
        ?.updateValueAndValidity({ emitEvent: false });
    });
  }

  public getFormControl(name: string): FormControl {
    return this.contactForm.get(name) as FormControl;
  }

  public handleIsOpenChange(isOpen: boolean): void {
    this.resetModal();
    this.isOpenChange.emit(isOpen);
  }

  public onBookCall(): void {
    window.open(
      'https://meetings-eu1.hubspot.com/meetings/manuel-zwittag',
      '_blank',
    );
  }

  public onShowContactForm(): void {
    this.showContactForm = true;
  }

  public onBackToOptions(): void {
    this.showContactForm = false;
  }

  public async onSubmitContact(): Promise<void> {
    if (this.contactForm.valid) {
      const formData: SupportContactData = {
        name: this.contactForm.get('name')?.value || undefined,
        email: this.contactForm.get('email')?.value || undefined,
        tgHandle: this.contactForm.get('tgHandle')?.value || undefined,
        info: this.contactForm.get('info')?.value || undefined,
      };

      // Remove empty string values
      Object.keys(formData).forEach(key => {
        if (formData[key as keyof SupportContactData] === '') {
          delete formData[key as keyof SupportContactData];
        }
      });

      await this.supportService.sendSupportRequest(formData);
      this.contactFormSent = true;
    } else {
      this.markFormGroupTouched();
    }
  }

  private resetModal(): void {
    this.showContactForm = false;
    this.contactForm.reset();
  }

  private markFormGroupTouched(): void {
    Object.keys(this.contactForm.controls).forEach(key => {
      this.contactForm.get(key)?.markAsTouched();
    });
  }

  public onDontShowAgainChange(dontShowAgain: boolean): void {
    this.dontShowAgain = dontShowAgain;
    localStorage.setItem(
      SupportModalComponent.STORAGE_KEY_HIDE,
      dontShowAgain.toString(),
    );
  }
}
